import { useCallback, useEffect, useRef, useState } from 'react';

/** Missing, malformed, or future storage values must not suppress reminders. */
export const parseLastShownAt = (value: string | null, now: number): number => {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp >= 0 && timestamp <= now
    ? timestamp
    : 0;
};

export const REMINDER_COOLDOWN_MS = 4 * 60 * 60 * 1000;

/**
 * A reminder cooldown shared across page loads and browser tabs: `canShow`
 * once `intervalMs` has passed since `recordShown` was last called.
 */
export function useStoredCooldown(
  storageKey: string,
  intervalMs = REMINDER_COOLDOWN_MS,
) {
  const [lastShownAt, setLastShownAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now);
  const [isVisible, setIsVisible] = useState(false);
  const hasUnpersistedTimestamp = useRef(false);

  useEffect(() => {
    const sync = () => {
      const currentTime = Date.now();
      setNow(currentTime);
      setIsVisible(document.visibilityState === 'visible');
      try {
        const stored = parseLastShownAt(
          window.localStorage.getItem(storageKey),
          currentTime,
        );
        if (hasUnpersistedTimestamp.current) {
          // Reads can succeed after a failed write; keep the newer cooldown.
          setLastShownAt((previous) => Math.max(previous ?? 0, stored));
        } else {
          setLastShownAt(stored);
        }
      } catch {
        // Keep the in-memory cooldown when browser storage is unavailable.
        setLastShownAt((previous) => previous ?? 0);
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey || event.key === null) sync();
    };
    sync();
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [storageKey]);

  useEffect(() => {
    if (lastShownAt === null) return;
    const remaining = lastShownAt + intervalMs - Date.now();
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => setNow(Date.now()), remaining);
    return () => window.clearTimeout(timer);
  }, [lastShownAt, intervalMs]);

  const recordShown = useCallback(() => {
    const timestamp = Date.now();
    setLastShownAt(timestamp);
    setNow(timestamp);
    try {
      window.localStorage.setItem(storageKey, String(timestamp));
      hasUnpersistedTimestamp.current = false;
    } catch {
      hasUnpersistedTimestamp.current = true;
    }
  }, [storageKey]);

  return {
    canShow:
      isVisible && lastShownAt !== null && now - lastShownAt >= intervalMs,
    recordShown,
  };
}
